import { motion } from "framer-motion";
import { Download, Clock, ArrowRight, Loader2, Sparkles, Check, Zap, Layers } from "lucide-react";
import { TOOLKIT } from "@/data/offers";
import { TOOLKIT_DETAILS } from "@/data/toolkitDetails";
import { TOOL_ICONS } from "@/components/landing/WhatYouGet";

const ICON_BY_KEY = Object.fromEntries(TOOLKIT.map((t) => [t.key, t]));

const Badge = ({ it }) => {
  if (it.available) return <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" />{it.kind === "guide" ? "Tailored for you" : "Ready"}</span>;
  if (it.status === "preparing") return <span className="flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-[12px] font-semibold text-sky-700"><Loader2 className="h-3.5 w-3.5 animate-spin" />Being tailored</span>;
  if (it.key === "survey") return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-800">Needs your diagnostic</span>;
  return <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[12px] font-semibold text-slate-600">Coming soon</span>;
};

const Preview = ({ p }) => {
  if (!p) return null;
  if (p.maps?.length) return (
    <div data-testid="toolkit-preview" className="flex flex-wrap gap-2">
      {p.maps.map((m, i) => <span key={m} className="rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0B1B33]"><span className="mr-1.5 text-[#0E9384]">{i + 1}</span>{m}</span>)}
    </div>
  );
  return p.sample ? (
    <div data-testid="toolkit-preview">
      <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0E9384]">{p.sample_label}</p>
      <p className="mt-1.5 line-clamp-4 whitespace-pre-line font-mono text-[13px] leading-relaxed text-slate-700">{p.sample}{p.sample.length >= 260 ? "…" : ""}</p>
    </div>
  ) : null;
};

const Action = ({ it, onDiagnostic }) => {
  if (it.available) return (
    <a data-testid={`my-toolkit-download-${it.key}`} href={it.url} target="_blank" rel="noreferrer" className="btn-teal flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-[14px] font-semibold">
      <Download className="h-4 w-4" /> Download PDF
    </a>
  );
  if (it.key === "survey") return (
    <button data-testid="my-toolkit-take-diagnostic" onClick={onDiagnostic} className="flex items-center justify-center gap-2 rounded-lg bg-[#0B1B33] px-5 py-3 text-[14px] font-semibold text-white">
      Take the 5-minute diagnostic <ArrowRight className="h-4 w-4" />
    </button>
  );
  return <span className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-100 px-5 py-3 text-[13px] font-medium text-slate-600"><Clock className="h-4 w-4" />{it.status === "preparing" ? "Usually ready in 1-3 minutes" : "We'll add it here"}</span>;
};

export const ToolkitCard = ({ it, i, onDiagnostic }) => {
  const meta = ICON_BY_KEY[it.key] || TOOLKIT[0];
  const d = TOOLKIT_DETAILS[it.key] || {};
  const { I, c } = TOOL_ICONS[meta.icon];
  const first = it.preview?.first_action || d.action;
  return (
    <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
      data-testid={`my-toolkit-item-${it.key}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-[0_18px_40px_-24px_rgba(11,27,51,0.35)]">
      <div className="flex items-start gap-4">
        <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${c}`}><I className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><h3 className="font-display text-[19px] font-bold leading-tight text-[#0B1B33]">{it.title}</h3><Badge it={it} /></div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-slate-700">{d.blurb || meta.text}</p>
        </div>
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-600">What's inside</p>
          <ul className="mt-2 space-y-1.5">{(d.inside || []).map((x) => <li key={x} className="flex gap-2 text-[14px] text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9384]" />{x}</li>)}</ul>
        </div>
        <div className="rounded-xl bg-[#F3FAF8] p-4">
          <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-600"><Sparkles className="h-3.5 w-3.5 text-[#0E9384]" />{it.preview ? "A peek at your version" : "Preview"}</p>
          {it.preview ? <Preview p={it.preview} /> : <p className="text-[13px] text-slate-600">{it.status === "preparing" ? "We're writing this for your role, experience and goal right now." : "Available as soon as it's ready."}</p>}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-600">
        <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{it.preview?.time_to_use || d.time}</span>
        {it.preview?.count ? <span className="flex items-center gap-1.5"><Layers className="h-4 w-4" />{it.preview.count} ready-to-use pieces</span> : null}
      </div>
      {first && <p data-testid={`toolkit-first-action-${it.key}`} className="mt-4 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-[14px] text-amber-900"><Zap className="mt-0.5 h-4 w-4 shrink-0" /><span><b>Your first action: </b>{first}</span></p>}
      <div className="mt-5 sm:self-start"><Action it={it} onDiagnostic={onDiagnostic} /></div>
    </motion.article>
  );
};
